class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );

    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      const regex = new RegExp(previousTerm, 'g');
      const matches = previousTerm ? currentButtonText.match(regex) : null;

      if (matches && matches.length > 1) {
        return;
      }

      const newButtonText = currentButtonText.replace(
        previousTerm,
        newTerm
      );

      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li, button.predictive-search__item'
      )
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i < allVisibleElements.length
    ) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }

      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', false);
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm
      .replace(/\s+/g, '-')
      .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();
    const searchSignal = this.abortController.signal;

    const searchUrl =
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product,query` +
      `&resources[limit]=10` +
      `&resources[limit_scope]=each` +
      `&resources[options][unavailable_products]=last` +
      `&resources[options][fields]=title,tag,product_type,variants.title,vendor,variants.sku,variants.barcode` +
      `&section_id=predictive-search`;

    fetch(searchUrl, {
      signal: searchSignal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            `Predictive Search Fehler: ${response.status}`
          );

          this.close();
          throw error;
        }

        return response.text();
      })
      .then(async (text) => {
        const parsedDocument = new DOMParser().parseFromString(
          text,
          'text/html'
        );

        const predictiveSection =
          parsedDocument.querySelector(
            '#shopify-section-predictive-search'
          );

        if (!predictiveSection) {
          throw new Error(
            'Predictive-Search-Section wurde nicht gefunden.'
          );
        }


        if (searchSignal.aborted) return;

        const strictTerm = searchTerm.trim().toLowerCase();
        const productList = predictiveSection.querySelector('#predictive-search-results-products-list');
        const normalizedTerm = ({
          tops: 'top',
          kochjacken: 'kochjacke',
          westen: 'weste',
          jacken: 'jacke',
          schuh: 'schuhe'
        })[strictTerm] || strictTerm;

        if (productList && ['top', 'kochjacke', 'schuhe', 'weste', 'jacke', 'langarm', 'kurzarm', 'speck', 'salami'].includes(normalizedTerm)) {
          [...productList.children].forEach((item) => {
            const title = item.dataset.searchTitle || '';
            const type = item.dataset.searchType || '';
            const tags = (item.dataset.searchTags || '').split('|').filter(Boolean);
            const hasTag = (tag) => tags.includes(tag);
            let keep = true;

            if (normalizedTerm === 'top') {
              keep = hasTag('top');
            } else if (normalizedTerm === 'kochjacke') {
              keep = hasTag('kochjacke') || hasTag('kat:kochjacken') || type.includes('kochjacke') || title.includes('kochjacke');
            } else if (normalizedTerm === 'schuhe') {
              keep = hasTag('schuhe') || type.includes('schuhe') || type.includes('clogs') || type.includes('custom sneakers') || title.includes('schuhe');
            } else if (normalizedTerm === 'weste') {
              keep = hasTag('weste') || title.includes('weste') || type.includes('weste');
            } else if (normalizedTerm === 'jacke') {
              const isChefJacket = hasTag('kochjacke') || hasTag('kat:kochjacken') || title.includes('kochjacke');
              keep = !isChefJacket && (hasTag('jacke') || title.includes('jacke') || type.includes('jacke'));
            } else if (normalizedTerm === 'speck') {
              keep = hasTag('speck') || type.includes('speck') || title.includes('speck');
            } else if (normalizedTerm === 'salami') {
              keep = hasTag('salami') || type.includes('salami') || title.includes('salami');
            } else if (normalizedTerm === 'langarm') {
              keep = hasTag('langarm') || title.includes('langarm') || /\blsl\b/.test(title);
            } else if (normalizedTerm === 'kurzarm') {
              keep = hasTag('kurzarm') || title.includes('kurzarm') || /\bssl\b/.test(title) || title.includes('short sleeve') || title.includes('short-sleeve');
            }

            if (!keep) item.remove();
          });
        }

        const resultsMarkup =
          predictiveSection.innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[
              queryKey
            ] = resultsMarkup;
          }
        );

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }

        console.error(
          'LA1970 Predictive Search:',
          error
        );

        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    if (!this.statusElement) return;

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    setTimeout(() => {
      this.statusElement?.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (countElement) {
      this.setLiveRegionText(
        countElement.textContent
      );
    }
  }

  getResultsMaxHeight() {
    const header =
      document.querySelector('.section-header');

    const headerBottom =
      header?.getBoundingClientRect().bottom || 0;

    this.resultsMaxHeight =
      window.innerHeight - headerBottom;

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', true);
    this.input.setAttribute(
      'aria-expanded',
      true
    );

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]'
      );

    if (selected) {
      selected.setAttribute(
        'aria-selected',
        false
      );
    }

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.removeAttribute('loading');
    this.removeAttribute('open');

    this.input.setAttribute(
      'aria-expanded',
      false
    );

    this.resultsMaxHeight = false;

    this.predictiveSearchResults.removeAttribute(
      'style'
    );
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}
